import { Button, Dialog, DialogActions, DialogContent } from "@mui/material";
import React, { useEffect, useState } from "react";
import { Form } from "react-final-form";
import { Text } from "../../../common/components/input/Text";
import { Loading } from "../../../common/components/Loading";
import { useActionRequest } from "../../../common/hooks/api";
import { Api } from "../../../common/service/Api";
import { OrganizationPublicId, UserRole } from "../../../common/service/server/Types";
import { Error as RequestsError, Success as RequestsSuccess } from "../../copy/requests";
import { requiredEmail, requiredString } from "../../util/validation";
import { Error } from "../Error";
import { CloseableDialogTitle } from "./CloseableDialogTitle";
import { SimpleSelect } from "../../../common/components/input/Select";
import { formatUserRole } from "../../util/formatters";
import { OrganizationUserDto } from "../../../common/service/server/dto/UserDto";

const inviteUserValidation = (values, existingUserEmails?: Set<string>) => {
  const errors = {} as any;

  requiredEmail(values, "email", errors);
  if (values.email?.toLowerCase().startsWith("privacy@")) {
    errors.email = 'Email addresses starting with "privacy@" are not allowed';
  }
  requiredString(values, "firstName", errors);
  requiredString(values, "lastName", errors);

  if (values.email && existingUserEmails?.has(values.email.toLowerCase())) {
    errors.email = "This user already has access to this organization";
  }

  return errors;
};

const inviteUserInitialValues = { email: "" };

const helperTextForRole = (role: UserRole): string => {
  switch (role) {
    case "HR":
      return "This user will only have access to HR onboarding and HR request data.";
    case "GENERAL":
      return "This user will have full access to Polaris, excluding HR information.";
    case "ADMIN":
      return "This user will have full access to Polaris.";
  }
  return "";
};

const InviteUserForm = ({ handleSubmit, onCancel, onlyShowRole, helpText, allowAddingHrUser }) => {
  const emailTextField = <Text field="email" label="Email Address" required />;

  const [currentRole, setCurrentRole] = useState<UserRole>("GENERAL");

  let roleOptions = [
    { value: "GENERAL", label: "General" },
    { value: "ADMIN", label: "Admin" },
  ];
  if (allowAddingHrUser) {
    roleOptions.push({ value: "HR", label: "HR" });
  }

  return (
    <form onSubmit={handleSubmit}>
      <DialogContent>
        <div className="flex-col">
          <div className="flex-row flex--expand">
            <Text autoFocus field="firstName" label="First Name" required />
            <Text field="lastName" label="Last Name" required />
          </div>
          {onlyShowRole === undefined ? (
            <div className="d-flex flex-row align-items-baseline" style={{ marginBottom: "4px" }}>
              {emailTextField}
              <SimpleSelect
                id="addUserRoleSelect"
                name="role"
                required
                includeBlank={false}
                options={roleOptions}
                variant={"standard"}
                disableUnderline={true}
                style={{
                  width: "110px",
                }}
                onChange={(e) => {
                  setCurrentRole(e.target.value as UserRole);
                }}
              />
            </div>
          ) : (
            emailTextField
          )}
          {helpText ? (
            <div className="neutral-500 text-t1">{helpText}</div>
          ) : (
            <div className="neutral-500 text-t1">
              {helperTextForRole(onlyShowRole || currentRole)}
            </div>
          )}
        </div>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel}>Cancel</Button>
        <Button type="submit" color="primary" variant="contained">
          {onlyShowRole ? `Add ${formatUserRole(onlyShowRole)} User` : "Add User"}
        </Button>
      </DialogActions>
    </form>
  );
};

type InviteUserDialogProps = {
  open: boolean;
  organizationId: OrganizationPublicId;
  onlyShowRole?: UserRole;
  helpText?: string;
  onClose: () => void;
  onDone: (invitedUser: OrganizationUserDto) => void;
  allowAddingHrUser?: boolean;
  showDefaultSnackbarNotifications?: boolean;
  existingUserEmails?: Set<string>;
};

export const InviteUserDialog = ({
  open,
  organizationId,
  onlyShowRole,
  helpText,
  onClose,
  onDone,
  allowAddingHrUser = true,
  showDefaultSnackbarNotifications = true,
  existingUserEmails,
}: InviteUserDialogProps) => {
  const {
    request,
    fetch: sendInviteRequest,
    resetState,
  } = useActionRequest({
    api: (values) => Api.organizationUser.inviteUser(organizationId, values),
    messages: showDefaultSnackbarNotifications
      ? {
          success: RequestsSuccess.InviteUser,
          defaultError: RequestsError.InviteUser,
        }
      : undefined,
    notifyError: true,
    notifySuccess: true,
    onSuccess: (result) => {
      onDone(result);
    },
    onError: (networkRequestState, error) => {
      networkRequestState.errorMessage =
        // @ts-ignore
        error?.response?.data?.message ??
        error?.message ??
        "There was an error while processing your request.";
    },
  });

  const onSubmit = async (values) => {
    // HACK: react final form doesn't seem to pick up the permissions value from the <Select> component, so just read it directly from the DOM
    if (onlyShowRole) {
      values.role = onlyShowRole;
    } else {
      values.role = (
        document.querySelector("#addUserRoleSelect + input") as HTMLInputElement
      ).value;
    }

    await sendInviteRequest(values);
  };

  useEffect(() => {
    if (open) {
      resetState();
    }
  }, [open, resetState]);

  return (
    <Dialog
      className="get-compliant-dialog"
      fullWidth={true}
      maxWidth={"sm"}
      open={open}
      onClose={onClose}
      aria-labelledby="invite-user-dialog-title"
    >
      <CloseableDialogTitle onClose={onClose}>
        {onlyShowRole ? `Add ${formatUserRole(onlyShowRole)} User` : "Add a User"}
      </CloseableDialogTitle>

      {request.running && (
        <DialogContent>
          <Loading />
        </DialogContent>
      )}

      {request.error && (
        <>
          <DialogContent>
            <Error message={request.errorMessage || RequestsError.InviteUser} />
          </DialogContent>
          <DialogActions>
            <Button onClick={onClose}>Close</Button>
          </DialogActions>
        </>
      )}

      {!request.started && (
        <Form
          onSubmit={onSubmit}
          initialValues={inviteUserInitialValues}
          validate={(values) => inviteUserValidation(values, existingUserEmails)}
          render={({ handleSubmit, form, ...props }) => (
            <InviteUserForm
              handleSubmit={(e) => {
                const result = handleSubmit(e);
                if (result && typeof result.then === "function") {
                  return result.then(
                    () => form.reset(),
                    () => {},
                  );
                }
                return result;
              }}
              {...props}
              onCancel={onClose}
              onlyShowRole={onlyShowRole}
              helpText={helpText}
              allowAddingHrUser={allowAddingHrUser}
            />
          )}
        />
      )}
    </Dialog>
  );
};
